// MUI
import { Box, Chip, Typography } from '@mui/material';
import { snakeCase } from 'lodash';

// Custom components
import AccordionWrapper from 'components/ChecklistReport/AccordionWrapper';
import ChecklistLogItem from 'components/ChecklistReport/ChecklistLogItem';
import { AccordionContentCnt } from 'components/ChecklistReport/HeaderItem/headerItem.style';
import { checkVisibilityWithText } from 'components/TaskChecklist/utils';
import CheckListHiddenItem from '../ChecklistLogItem/ChecklistHiddenItem';
import { useMemo } from 'react';
import { STEP_TYPES } from 'utils/templates';
import { scoreSummaryStyles } from '../scoreSummary/styles';
import { getMatchingScoreConfig } from 'utils/templates/scoringConfig';

const HeaderItem = ({
  headerItem,
  headerIndex,
  logItems,
  sectionRefs,
  template,
  checklistLog,
  isPublic,
  isScoring,
  flaggedCategories = [],
  conditions = null,
  checkListLogHashes,
  locationGroupHash = {},
  innerContent = false,
}) => {
  const isSectionHidden = useMemo(() => {
    return checkVisibilityWithText(
      conditions,
      headerItem,
      checkListLogHashes,
      true,
      locationGroupHash,
    );
  }, [conditions, headerItem]);

  const renderFollowUpContent = (item: any, index: any, followup = false) => {
    // Otherwise render the regular checklist item
    return item?.type === STEP_TYPES.HEADER ? (
      <Box
        sx={{
          backgroundColor: '#EBEAFF !important',
          height: '36px',
          padding: '0px 10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography style={{ fontWeight: 700, fontSize: '15px' }}>
          {item?.title}
        </Typography>

        {template?.isScoring && headerItem?.score?.total >= 0 && (
          <Chip
            label={`Score: ${headerItem?.score?.percentage}% (${headerItem?.score?.earned} of ${headerItem?.score?.total} pts)`}
            size="small"
            sx={{
              background: '#CCCBFF',
              fontWeight: 700,
              fontSize: '13px',
            }}
          />
        )}
      </Box>
    ) : (
      <Box
        sx={{
          borderBottom:
            followup || index === logItems?.[headerIndex]?.length - 1
              ? ''
              : '1px solid rgba(224, 224, 224, 1)',
          padding: followup ? '8px 0px 0px 0px' : '8px 0px 8px 24px',
        }}
        className="checklist-log-item"
      >
        <Box
          sx={{
            '&:hover': {
              background: 'rgba(235, 234, 255, 1)',
              '& div.corrective-task': {
                display: 'flex',
              },
            },
          }}
        >
          <ChecklistLogItem
            isHidden={checkVisibilityWithText(
              conditions,
              item,
              checkListLogHashes,
              true,
              locationGroupHash,
            )}
            checklistLog={checklistLog}
            logItem={item}
            template={template}
            isPublic={isPublic}
            hideCorrectiveTask={false}
            flaggedCategories={flaggedCategories}
            checkListLogHashes={checkListLogHashes}
          />
        </Box>

        {/* Render followup items recursively if they exist */}
        {item?.followUps?.map((followupItem: any, followupIndex: number) => (
          <Box key={`${index}-${followupIndex}`}>
            {renderFollowUpContent(followupItem, followupIndex, true)}
          </Box>
        ))}
      </Box>
    );
  };

  const renderContent = (item: any, index: any, followup = false) => {
    // If the item is a header (has items array), render another HeaderItem
    if (item?.TaskChecklistItems) {
      return (
        <HeaderItem
          headerItem={item}
          headerIndex={`${headerIndex}-${index}`}
          logItems={logItems}
          sectionRefs={sectionRefs}
          template={template}
          checklistLog={checklistLog}
          isPublic={isPublic}
          isScoring={isScoring}
          flaggedCategories={flaggedCategories}
          conditions={conditions}
          checkListLogHashes={checkListLogHashes}
          locationGroupHash={locationGroupHash}
          innerContent={innerContent}
        />
      );
    }

    // Otherwise render the regular checklist item
    return (
      <Box
        sx={{
          borderBottom:
            followup || index === logItems?.[headerIndex]?.length - 1
              ? ''
              : '1px solid rgba(224, 224, 224, 1)',
          padding: followup ? '8px 0px 0px 0px' : '8px 0px 8px 24px',
        }}
        className="checklist-log-item"
      >
        <Box
          sx={{
            '&:hover': {
              background: 'rgba(235, 234, 255, 1)',
              '& div.corrective-task': {
                display: 'flex',
              },
            },
          }}
        >
          <ChecklistLogItem
            isHidden={checkVisibilityWithText(
              conditions,
              item,
              checkListLogHashes,
              true,
              locationGroupHash,
            )}
            checklistLog={checklistLog}
            logItem={item}
            template={template}
            isPublic={isPublic}
            hideCorrectiveTask={false}
            flaggedCategories={flaggedCategories}
            checkListLogHashes={checkListLogHashes}
          />
        </Box>

        {/* Render followup items recursively if they exist */}
        {item?.followUps?.length > 0 && (
          <Box
            sx={{
              backgroundColor: '#EBEAFF !important',
              height: '36px',
              padding: '0px 16px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography style={{ fontWeight: 700, fontSize: '15px' }}>
              Followup Questions:
            </Typography>
          </Box>
        )}

        {item?.followUps?.map((followupItem: any, followupIndex: number) => (
          <div
            style={{
              borderLeft: '2px solid #EBEAFF',
              borderRight: '2px solid #EBEAFF',
              padding: '0px 15px',
            }}
          >
            <Box key={`${index}-${followupIndex}`}>
              {renderFollowUpContent(followupItem, followupIndex, true)}
            </Box>
          </div>
        ))}
      </Box>
    );
  };

  const matchedConfig = getMatchingScoreConfig(
    headerItem?.score?.percentage,
    template?.HotelChecklist?.scoreConfig,
  );

  return (
    <Box
      id={snakeCase(headerItem?.description || headerItem?.title)}
      key={headerIndex}
      ref={sectionRefs[headerIndex + isScoring ? 2 : 1]}
      sx={{
        marginBottom: '6px',
        ...(innerContent && {
          '& .MuiAccordionDetails-root': {
            padding: 0,
          },
        }),
      }}
    >
      {isSectionHidden ? (
        <CheckListHiddenItem
          info={isSectionHidden}
          type="Section"
          title={headerItem?.description || headerItem?.title}
        />
      ) : (
        <AccordionWrapper
          innerSection={innerContent}
          headerName={headerItem?.description || headerItem?.title}
          progress={headerItem.progress}
          headerEndRenderer={
            template?.isScoring && headerItem?.score?.total >= 0 ? (
              <Box
                component="span"
                sx={{
                  ...scoreSummaryStyles.overallScoreChip(
                    headerItem?.score?.percentage,
                  ),
                  backgroundColor: `${
                    matchedConfig?.color ?? '#1f1e1ee0'
                  } !important`,
                  color: 'white !important',
                }}
              >
                {`Score: ${headerItem?.score?.percentage}% (${headerItem?.score?.earned} of ${headerItem?.score?.total} pts)`}
              </Box>
            ) : null
          }
        >
          <AccordionContentCnt
            sx={{
              ...(innerContent && {
                padding: 0,
              }),
            }}
          >
            <Box
              sx={{
                backgroundColor: 'white !important',
              }}
            >
              {headerItem?.TaskChecklistItems?.map((item: any, index: any) =>
                renderContent(item, index),
              )}
            </Box>

            {headerItem?.subSections?.map((item: any, index: any) =>
              renderContent(item, index),
            )}
          </AccordionContentCnt>
        </AccordionWrapper>
      )}
    </Box>
  );
};

export default HeaderItem;
